import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { useSettings } from '@/hooks/useSettings';
import { generateColorScale } from '@/utils/colorScale';
import { useAuthStore } from '@/features/auth';
import { applyColorSettings } from '@/features/admin/utils/colorSchemes';
import i18n from '@/i18n';

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { data: settings } = useSettings();
  const user = useAuthStore((s) => s.user);

  useEffect(() => {
    if (!settings) return;
    const root = document.documentElement;

    // Only override CSS theme tokens if the server color differs from the
    // default Apple Blue (#007AFF). The @theme block already defines the
    // correct hand-tuned Apple scale — the auto-generator darkens too much.
    const DEFAULT_PRIMARY = '#007AFF';
    const DEFAULT_ACCENT = '#28CD41';

    if (settings.primaryColor && settings.primaryColor.toUpperCase() !== DEFAULT_PRIMARY) {
      const scale = generateColorScale(settings.primaryColor);
      for (const [shade, value] of Object.entries(scale)) {
        root.style.setProperty(`--color-primary-${shade}`, value);
      }
    }

    if (settings.colorAccent && settings.colorAccent.toUpperCase() !== DEFAULT_ACCENT) {
      const scale = generateColorScale(settings.colorAccent);
      for (const [shade, value] of Object.entries(scale)) {
        root.style.setProperty(`--color-accent-${shade}`, value);
      }
    }

    // Liquid Glass theme: pure black labels, darker secondaries, more translucent surfaces
    const isLiquidGlass = settings.themePreset?.startsWith('liquid-glass');
    if (isLiquidGlass) {
      root.style.setProperty('--color-label-primary', 'rgba(0, 0, 0, 1.0)');
      root.style.setProperty('--color-label-secondary', 'rgba(0, 0, 0, 0.55)');
      root.style.setProperty('--color-label-tertiary', 'rgba(0, 0, 0, 0.30)');
      root.style.setProperty('--color-label-quaternary', 'rgba(0, 0, 0, 0.18)');
      root.style.setProperty('--color-surface-secondary', 'rgba(246, 246, 250, 1.0)');
      root.style.setProperty('--glass-bg', 'rgba(255, 255, 255, 0.50)');
      root.style.setProperty('--glass-bg-nav', 'rgba(255, 255, 255, 0.65)');
      root.classList.add('liquid-glass');
    } else {
      root.classList.remove('liquid-glass');
    }

    // Per-section color schemes (user nav, bottom nav, pages, admin nav, admin pages)
    applyColorSettings(settings, root);

    // Apply system default language only for unauthenticated pages.
    // When a user is logged in (even without preferredLanguage, e.g. onboarding),
    // keep whatever language was active on the login page.
    if (settings.defaultLanguage && !user) {
      i18n.changeLanguage(settings.defaultLanguage);
    }
  }, [settings, user]);

  return <>{children}</>;
}
